import {
  shadowSpectrumById,
  SHADOW_SPECTRUM_IDS,
  SHADOW_RESULT_TEMPLATES,
  fillTemplate,
  type ShadowResultType,
} from '../data/shadowSpectra';

// First-pass thresholds (spec §4) — named so they're easy to tune once real
// responses exist. All operate on quadrant/activation percentages (0–100),
// except DEADZONE which is on the −9…+9 Direction scale.
export const SHADOW_THRESHOLDS = {
  LOW: 35,
  HIGH: 60,
  SHADOW_HI: 55,
  DEADZONE: 1.5,
} as const;

// Repression overlay thresholds (spec §4).
export const SHADOW_REPRESSION = {
  LOW_ACT: 30, // the disowned side's activation must be below this
  HIGH_ACT: 55, // the dominant side's activation must be above this
  LOW_POS: 35, // the disowned side's healthy expression is barely available
} as const;

/** Quadrant percentages (0–100) for one spectrum. */
export interface QuadrantPcts {
  LP: number;
  LS: number;
  RP: number;
  RS: number;
}

export interface SpectrumResult {
  spectrumId: string;
  name: string;
  leftPole: string;
  rightPole: string;
  leftPositive: number;
  leftShadow: number;
  rightPositive: number;
  rightShadow: number;
  leftActivation: number;
  rightActivation: number;
  directionScore: number; // −9 … +9 (+ = right pole)
  shadowLoad: number; // 0–100
  integrationScore: number; // 0–100
  repressedCounterpart: boolean;
  resultType: ShadowResultType;
  resultLabel: string;
  interpretation: string;
  growthDirection: string;
  growthPractice: string;
}

export interface ShadowOverall {
  integration: number;
  shadowLoad: number;
  status: string;
  typeCounts: Record<string, number>;
  topStrengths: string[];
  topRisks: string[];
  topPractices: string[];
}

export interface ShadowMap {
  spectra: SpectrumResult[];
  overall: ShadowOverall;
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
const round1 = (n: number) => Math.round(n * 10) / 10;

/** Mean of a 1–5 Likert pair → 0–100% (spec §4). */
export function quadrantPct(itemValues: number[]): number {
  if (!itemValues.length) return 0;
  const mean = itemValues.reduce((s, v) => s + v, 0) / itemValues.length;
  return clamp(((mean - 1) / 4) * 100, 0, 100);
}

/** Pure math for one spectrum from its 4 quadrant %s (spec §4). */
export function deriveSpectrum(q: QuadrantPcts): {
  leftActivation: number;
  rightActivation: number;
  directionScore: number;
  shadowLoad: number;
  integrationScore: number;
  repressedCounterpart: boolean;
  resultType: ShadowResultType;
} {
  const { LP, LS, RP, RS } = q;
  const { LOW, HIGH, SHADOW_HI, DEADZONE } = SHADOW_THRESHOLDS;

  const leftActivation = (LP + LS) / 2;
  const rightActivation = (RP + RS) / 2;
  const directionScore = round1(((rightActivation - leftActivation) / 100) * 9);
  const shadowLoad = (LS + RS) / 2;
  const integrationScore = clamp((LP + RP) / 2 - (LS + RS) / 2, 0, 100);

  // Repression overlay: the disowned side is barely active and its healthy form
  // is barely available, while the other side is strongly active.
  const lowAct = Math.min(leftActivation, rightActivation);
  const highAct = Math.max(leftActivation, rightActivation);
  const lowSidePositive = leftActivation < rightActivation ? LP : RP;
  const repressedCounterpart =
    lowAct < SHADOW_REPRESSION.LOW_ACT &&
    highAct > SHADOW_REPRESSION.HIGH_ACT &&
    lowSidePositive < SHADOW_REPRESSION.LOW_POS;

  // Result-type decision tree (top-down, first match wins).
  const posMax = Math.max(LP, RP);
  const shadowMax = Math.max(LS, RS);
  let resultType: ShadowResultType;

  if (LP < LOW && LS < LOW && RP < LOW && RS < LOW) {
    resultType = 'low_access';
  } else if (posMax >= HIGH && shadowMax >= SHADOW_HI) {
    resultType = 'inner_conflict';
  } else if (integrationScore >= HIGH && shadowLoad < LOW) {
    resultType = 'integrated_flexible';
  } else if (Math.abs(directionScore) < DEADZONE) {
    resultType = integrationScore >= LOW ? 'integrated_flexible' : 'low_access';
  } else {
    const lean: 'left' | 'right' = directionScore < 0 ? 'left' : 'right';
    const shadowOnLean = lean === 'left' ? LS : RS;
    const posOnLean = lean === 'left' ? LP : RP;
    if (shadowOnLean >= SHADOW_HI || shadowOnLean > posOnLean) {
      resultType = lean === 'left' ? 'left_shadow_dominant' : 'right_shadow_dominant';
    } else {
      resultType = lean === 'left' ? 'healthy_left_leaning' : 'healthy_right_leaning';
    }
  }

  // Overlay: a healthy lean that disowns its counterpart becomes repressed_counterpart.
  if (repressedCounterpart && (resultType === 'healthy_left_leaning' || resultType === 'healthy_right_leaning')) {
    resultType = 'repressed_counterpart';
  }

  return { leftActivation, rightActivation, directionScore, shadowLoad, integrationScore, repressedCounterpart, resultType };
}

/** Full per-spectrum result: math + pole/template/practice copy (spec §5/§9). */
export function buildSpectrumResult(spectrumId: string, q: QuadrantPcts): SpectrumResult {
  const spectrum = shadowSpectrumById[spectrumId];
  const left = spectrum?.leftPole ?? 'Left';
  const right = spectrum?.rightPole ?? 'Right';
  const d = deriveSpectrum(q);

  // For repressed_counterpart, dominant = higher-activation pole, opposite = disowned.
  const dominant = d.leftActivation >= d.rightActivation ? left : right;
  const opposite = dominant === left ? right : left;
  const poles = { left, right, dominant, opposite };

  const template = SHADOW_RESULT_TEMPLATES[d.resultType];

  return {
    spectrumId,
    name: spectrum?.name ?? spectrumId,
    leftPole: left,
    rightPole: right,
    leftPositive: Math.round(q.LP),
    leftShadow: Math.round(q.LS),
    rightPositive: Math.round(q.RP),
    rightShadow: Math.round(q.RS),
    leftActivation: Math.round(d.leftActivation),
    rightActivation: Math.round(d.rightActivation),
    directionScore: d.directionScore,
    shadowLoad: Math.round(d.shadowLoad),
    integrationScore: Math.round(d.integrationScore),
    repressedCounterpart: d.repressedCounterpart,
    resultType: d.resultType,
    resultLabel: fillTemplate(template.label, poles),
    interpretation: fillTemplate(template.read, poles),
    growthDirection: fillTemplate(template.growth, poles),
    growthPractice: spectrum?.integrationPractice ?? '',
  };
}

const HEALTHY_TYPES: ShadowResultType[] = ['integrated_flexible', 'healthy_left_leaning', 'healthy_right_leaning'];
const RISK_TYPES: ShadowResultType[] = ['left_shadow_dominant', 'right_shadow_dominant', 'repressed_counterpart'];

/** Overall read label from the mean integration. */
export function overallStatus(integration: number): string {
  if (integration >= SHADOW_THRESHOLDS.HIGH) return 'Wide range';
  if (integration >= SHADOW_THRESHOLDS.LOW) return 'Growing range';
  return 'Building awareness';
}

/** Aggregate the completed per-spectrum results into the full-map payload (spec §4). */
export function buildShadowMap(spectra: SpectrumResult[]): ShadowMap {
  const n = spectra.length || 1;
  const integration = Math.round(spectra.reduce((s, r) => s + r.integrationScore, 0) / n);
  const shadowLoad = Math.round(spectra.reduce((s, r) => s + r.shadowLoad, 0) / n);

  const typeCounts: Record<string, number> = {};
  for (const r of spectra) typeCounts[r.resultType] = (typeCounts[r.resultType] || 0) + 1;

  const topStrengths = spectra
    .filter(r => HEALTHY_TYPES.includes(r.resultType))
    .sort((a, b) => b.integrationScore - a.integrationScore)
    .slice(0, 3)
    .map(r => r.name);

  const topRisks = spectra
    .filter(r => RISK_TYPES.includes(r.resultType) || r.resultType === 'inner_conflict')
    .sort((a, b) => b.shadowLoad - a.shadowLoad)
    .slice(0, 3)
    .map(r => r.name);

  // Practices to work on: the 3 lowest-integration spectra (most room to grow).
  const topPractices = [...spectra]
    .sort((a, b) => a.integrationScore - b.integrationScore)
    .slice(0, 3)
    .map(r => r.growthPractice)
    .filter(Boolean);

  return {
    spectra,
    overall: {
      integration,
      shadowLoad,
      status: overallStatus(integration),
      typeCounts,
      topStrengths,
      topRisks,
      topPractices,
    },
  };
}

// ── Modular overview (merge per-spectrum attempts into the full map) ──────────
// Mirrors lifeBalanceScoring.mergeLatestDomainResults: the modular model takes
// each spectrum as its own attempt, so the whole map = the latest take of each.

export interface SpectrumMeta {
  completedAt: string;
  resultType: ShadowResultType;
  resultLabel: string;
  integration: number;
  shadowLoad: number;
}

export interface ShadowOverview {
  /** Merged map from the latest take of each spectrum (null if nothing completed). */
  result: ShadowMap | null;
  /** Per-spectrum freshness/result for the picker + history chips. */
  perSpectrum: Record<string, SpectrumMeta>;
}

export function mergeLatestSpectrumResults(
  attempts: Array<{ completedAt: string; result: ShadowMap | null | undefined }>,
): ShadowOverview {
  const sorted = attempts
    .filter(a => a.result && a.completedAt)
    .slice()
    .sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());

  const spectra: SpectrumResult[] = [];
  const perSpectrum: Record<string, SpectrumMeta> = {};

  for (const id of SHADOW_SPECTRUM_IDS) {
    const found = sorted.find(a => a.result!.spectra.some(s => s.spectrumId === id));
    if (!found) continue;
    const sr = found.result!.spectra.find(s => s.spectrumId === id)!;
    perSpectrum[id] = {
      completedAt: found.completedAt,
      resultType: sr.resultType,
      resultLabel: sr.resultLabel,
      integration: sr.integrationScore,
      shadowLoad: sr.shadowLoad,
    };
    spectra.push(sr);
  }

  return { result: spectra.length ? buildShadowMap(spectra) : null, perSpectrum };
}

/** Direction-bar zone for a −9…+9 score (visual aid only, spec §4). */
export type DirectionZone = 'shadow_left' | 'healthy_left' | 'integration' | 'healthy_right' | 'shadow_right';
export function directionZone(dir: number): DirectionZone {
  if (dir <= -6) return 'shadow_left';
  if (dir < -3) return 'healthy_left';
  if (dir <= 3) return 'integration';
  if (dir < 6) return 'healthy_right';
  return 'shadow_right';
}
